import { useQuery } from '@tanstack/react-query';
import { Alert, Spin } from 'antd';
import queryString from 'query-string';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { resetProps, updateProps } from '../../contextRedux/Agent/AgentModel';
import { setAgents } from '../../contextRedux/Agent/AgentStore';
import { setCompanies } from '../../contextRedux/Company/CompanyStore';
import { setWorkPlaces } from '../../contextRedux/WorkPlace/WorkPlaceStore';
import { AnimateButton } from '../../customComponents/button/CustomButton';
import AgentService from '../../services/agent/AgentService';
import CompanyService from '../../services/company/CompanyService';
import WorkPlaceService from '../../services/workPlace/WorkPlaceService';
import { RootState } from '../../store';
import '../Agent/Agent.css';
import AgentModal from './AgentModal/AgentModal';
import AgentTable from './AgentTable/AgentTable';

interface AgentProps {}

const Agent: React.FC<AgentProps> = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const searchTerm = useSelector((state: RootState) => state.models?.agent.props.searchTerm);
  const skip = useSelector((state: RootState) => state.models?.agent.props.skip);
  const take = useSelector((state: RootState) => state.models?.agent.props.take);
  const refresh = useSelector((state: RootState) => state.models?.agent.props.refresh);

  const [isParamsInitialized, setIsParamsInitialized] = useState<boolean>(false);

  const previousParamsRef = useRef({ term: '', page: '', pageSize: '' });

  useEffect(() => {
    const queryParams = queryString.parse(location.search);

    const termInUrl = (queryParams.term as string) || '';
    const pageInUrl = Number(queryParams.page) || 1;
    const pageSizeInUrl = Number(queryParams.pageSize) || take;

    const calculatedSkip = (pageInUrl - 1) * pageSizeInUrl;

    dispatch(updateProps({ field: 'searchTerm', value: termInUrl }));
    dispatch(updateProps({ field: 'skip', value: calculatedSkip }));
    dispatch(updateProps({ field: 'take', value: pageSizeInUrl }));

    previousParamsRef.current = { term: termInUrl, page: pageInUrl.toString(), pageSize: pageSizeInUrl.toString() };
    setIsParamsInitialized(true);
  }, [dispatch, location.search, take]);

  useEffect(() => {
    if (isParamsInitialized) {
      const queryParams = queryString.parse(location.search);
      const currentPage = Math.floor(skip / take) + 1;

      if (
        searchTerm !== previousParamsRef.current.term ||
        currentPage.toString() !== previousParamsRef.current.page ||
        take.toString() !== previousParamsRef.current.pageSize
      ) {
        const params = {
          ...queryParams,
          term: searchTerm || undefined,
          page: currentPage > 1 ? currentPage.toString() : undefined,
          pageSize: take !== 10 ? take.toString() : undefined,
        };

        const newSearch = queryString.stringify(params);
        navigate(`${location.pathname}?${newSearch}`, { replace: true });

        previousParamsRef.current = {
          term: searchTerm || '',
          page: currentPage.toString(),
          pageSize: take.toString(),
        };
      }
    }
  }, [searchTerm, skip, take, isParamsInitialized, navigate, location.pathname, location.search]);

  const {
    data: _dates,
    isFetching: isAgentsLoading,
    isError: isErrorOnFetchAgents,
    error: agentsError,
    refetch,
  } = useQuery({
    queryKey: ['agents', searchTerm, skip, take, refresh],
    queryFn: () =>
      AgentService.search({
        IsCompanyIncluded: true,
        IsWorkPlacesIncluded: true,
        termen: searchTerm || undefined,
        skip,
        take,
      }).then((data: any) => {
        dispatch(updateProps({ field: 'totalItems', value: data?.totalItems || 0 }));
        dispatch(setAgents(data?.response));
        return data?.response;
      }),
    enabled: isParamsInitialized,
    refetchOnWindowFocus: false,
    staleTime: 0,
    refetchOnMount: true,
    refetchOnReconnect: true,
  });

  const {
    data: _companies,
    isFetching: isCompaniesLoading,
    isError: isErrorOnFetchCompanies,
    error: companiesError,
  } = useQuery({
    queryKey: ['companies'],
    queryFn: () =>
      CompanyService.search({}).then((data: any) => {
        dispatch(setCompanies(data?.response));
        return data?.response;
      }),
    enabled: isParamsInitialized,
    refetchOnWindowFocus: false,
    staleTime: 0,
    refetchOnMount: true,
    refetchOnReconnect: true,
  });

  const {
    data: _workPlaces,
    isFetching: isWorkPlacesLoading,
    isError: isErrorOnFetchWorkPlaces,
    error: workPlacesError,
  } = useQuery({
    queryKey: ['workPlaces'],
    queryFn: () =>
      WorkPlaceService.search({}).then((data: any) => {
        dispatch(setWorkPlaces(data?.response));
        return data?.response;
      }),
    enabled: isParamsInitialized,
    refetchOnWindowFocus: false,
    staleTime: 0,
    refetchOnMount: true,
    refetchOnReconnect: true,
  });

  useEffect(() => {
    if (isParamsInitialized && searchTerm === '') {
      refetch();
    }
  }, [searchTerm, isParamsInitialized, refetch]);

  const handleReset = () => {
    dispatch(
      resetProps({
        isModalVisible: true,
        refresh: true,
        searchTerm,
        skip,
        take,
      })
    );
  };

  if (isAgentsLoading || isCompaniesLoading || isWorkPlacesLoading) {
    return <Spin size='large' className='spinner' />;
  }

  if (isErrorOnFetchAgents) {
    return <Alert message={agentsError.message} type='error' />;
  }
  if (isErrorOnFetchCompanies) {
    return <Alert message={companiesError.message} type='error' />;
  }
  if (isErrorOnFetchWorkPlaces) {
    return <Alert message={workPlacesError.message} type='error' />;
  }

  return (
    <>
      <h1 style={{ marginBottom: 50 }}>Persoane De Contact</h1>
      <AnimateButton onClick={() => handleReset()}>Adauga</AnimateButton>
      <AgentModal />
      <AgentTable />
    </>
  );
};

export default Agent;
